import { useEffect, useMemo, useState } from "react";
import { Form, FormGroup, Input, Label, Spinner } from "reactstrap";
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor";
import CustomSelect from "../../../components/CustomSelect";
import { fetchExportCustomFields } from "../../adminExports/data/customFieldsService";
import { resolveEffectiveTemplateState } from "../../adminExports/data/customFieldsTree";

const PINNED_SECTION = "Section 1";
const TYPE_NUMBER = "Number";
const TYPE_CURRENCY = "Currency";
const TYPE_DATE = "Date";
const COMPACT_NUMERIC_INPUT_STYLE = { width: "100px", maxWidth: "100%" };
const COMPACT_DATE_INPUT_STYLE = { width: "120px", maxWidth: "100%" };

const normalizeOptionsToStrings = (options) => {
  if (!Array.isArray(options)) return [];

  return options
    .map((option) => (typeof option === "string" ? option.trim() : ""))
    .filter((value) => value !== "");
};

const normalizeSectionName = (s) => (s || "").trim() || PINNED_SECTION;

const toDateInputValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const ymdPrefixMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymdPrefixMatch) return ymdPrefixMatch[1];

  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return "";

  return new Date(parsed).toISOString().slice(0, 10);
};

const hasAnyTextContent = (value) => {
  if (value === null || value === undefined || value === "") return false;
  const container = document.createElement("div");
  container.innerHTML = String(value);
  container.querySelectorAll("br").forEach((b) => b.remove());
  return (container.textContent || "").length > 0;
};

const TemplateInputs = ({
  selectedTemplate,
  sectionValues,
  onInputChanged,
  saveStatus,
  activeSectionOverride,
  hideSectionTabs = false,
  allowWhenLocked = false,
}) => {
  const [activeSection, setActiveSection] = useState("");
  const [globalFields, setGlobalFields] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadGlobalFields = async () => {
      const data = await fetchExportCustomFields();
      if (!isMounted) return;
      setGlobalFields(Array.isArray(data) ? data : []);
    };

    loadGlobalFields();

    return () => {
      isMounted = false;
    };
  }, []);

  const globalById = useMemo(() => {
    const map = new Map();
    (Array.isArray(globalFields) ? globalFields : []).forEach((field) => {
      if (field?.id === undefined || field?.id === null) return;
      map.set(String(field.id), field);
    });
    return map;
  }, [globalFields]);

  // Repairs the template on load: any field not yet placed anywhere in the layout tree (e.g. a
  // template saved before customFieldsLayout existed) is treated as if it were in an "Inputs" tab.
  const effectiveState = useMemo(
    () => resolveEffectiveTemplateState(selectedTemplate?.customFields, selectedTemplate?.customSections, selectedTemplate?.customFieldsLayout),
    [selectedTemplate]
  );
  const parsedSections = effectiveState.sections;
  const parsedFields = effectiveState.fields;
  const parsedLayout = effectiveState.layout;

  const sectionsByGuid = useMemo(() => new Map(parsedSections.map((s) => [s.guid, s])), [parsedSections]);
  const fieldLinkByGuid = useMemo(() => new Map(parsedFields.map((f) => [f.guid, f])), [parsedFields]);

  // Flat merged field metadata (global field details merged onto each template link), used for
  // ad-hoc field lookups (e.g. generateFieldKey) that don't need the section/region hierarchy.
  const customFields = useMemo(() => {
    return parsedFields
      .map((link) => {
        const globalField = globalById.get(String(link.customFieldId));
        if (!globalField) return null;

        const type = globalField?.type === "TextArea(WYSIWYG)" ? "TextArea" : globalField?.type;
        return {
          ...globalField,
          id: globalField.id,
          type,
          name: globalField?.name ?? globalField?.label ?? "",
          label: globalField?.label ?? globalField?.name ?? "",
        };
      })
      .filter(Boolean);
  }, [parsedFields, globalById]);

  // Maps each root tab name -> its render-ready children, built directly from the layout tree.
  // A section node becomes a "Subsection" box (its own bordered group, no visibility toggle),
  // while a field node is the merged global field data — recursing into `children` only when it
  // resolves to a Region.
  const sectionsByTab = useMemo(() => {
    const buildRenderNode = (node) => {
      if (node.type === "section") {
        const section = sectionsByGuid.get(node.id);
        return {
          id: `subsection-${node.id}`,
          type: "Subsection",
          label: normalizeSectionName(section?.name),
          children: (node.children || []).map(buildRenderNode).filter(Boolean),
        };
      }

      const link = fieldLinkByGuid.get(node.id);
      const globalField = link ? globalById.get(String(link.customFieldId)) : null;
      if (!globalField) return null;

      const type = globalField?.type === "TextArea(WYSIWYG)" ? "TextArea" : globalField?.type;
      const merged = {
        ...globalField,
        id: globalField.id,
        type,
        name: globalField?.name ?? globalField?.label ?? "",
        label: globalField?.label ?? globalField?.name ?? "",
      };

      if (type === "Region") {
        merged.children = (node.children || []).map(buildRenderNode).filter(Boolean);
      }

      return merged;
    };

    const map = new Map();
    (parsedLayout || []).forEach((rootNode) => {
      if (rootNode.type !== "section") return;
      const section = sectionsByGuid.get(rootNode.id);
      const tabName = normalizeSectionName(section?.name);
      map.set(tabName, (rootNode.children || []).map(buildRenderNode).filter(Boolean));
    });
    return map;
  }, [parsedLayout, sectionsByGuid, fieldLinkByGuid, globalById]);

  // A tab only appears when its subtree resolves to at least one real field (a "Subsection" node
  // on its own, with no fields anywhere inside it, doesn't count).
  const treeHasField = (nodes) => (nodes || []).some((n) => (n.type === "Subsection" ? treeHasField(n.children) : true));

  const sectionTabs = useMemo(() => {
    const tabs = [];
    (parsedLayout || []).forEach((rootNode) => {
      if (rootNode.type !== "section") return;
      const section = sectionsByGuid.get(rootNode.id);
      const tabName = normalizeSectionName(section?.name);
      if (!tabs.includes(tabName) && treeHasField(sectionsByTab.get(tabName))) {
        tabs.push(tabName);
      }
    });
    return tabs;
  }, [parsedLayout, sectionsByGuid, sectionsByTab]);

  const generateFieldKey = (fieldName) => {
    const fieldDef = customFields.find((s) => s.field === fieldName);

    if (!fieldDef) {
      return fieldName;
    }

    if (hasAnyTextContent(fieldDef.defaultValue) || fieldDef.salesforceDefaultValue) {
      const currentValue = (sectionValues?.[fieldName] ?? "").toString();
      if (!hasAnyTextContent(currentValue)) {
        return fieldName + "_empty";
      }
      return fieldName;
    }

    return fieldName;
  };

  useEffect(() => {
    if (!sectionTabs.length) {
      setActiveSection("");
      return;
    }

    if (activeSectionOverride && sectionTabs.includes(activeSectionOverride)) {
      setActiveSection(activeSectionOverride);
      return;
    }

    setActiveSection((prev) => (prev && sectionTabs.includes(prev) ? prev : sectionTabs[0]));
  }, [sectionTabs, activeSectionOverride]);

  const renderFieldRow = (s, isLast) => (
    <FormGroup key={s.id} className={`row align-items-start ${isLast ? "mb-0" : "mb-3"}`}>
      <Label htmlFor={s.id} className="fw-semibold col-sm-4 col-md-4 mb-0 text-start">
        {s.label}:
      </Label>

      <div className="col-sm-8 col-md-8">
        {s.type === "TextArea" && (
          <RichTextEditor
            key={generateFieldKey(s.field)}
            value={(sectionValues?.[s.field] ?? "").toString()}
            onChange={(html) => onInputChanged?.(s.field, html)}
            placeholder="Enter Text"
            minHeight={180}
            className="border"
            ariaLabel={s.label}
          />
        )}

        {s.type === "Text" && (
          <Input
            id={s.id}
            type="text"
            value={(sectionValues?.[s.field] ?? "").toString()}
            onChange={(e) => onInputChanged?.(s.field, e.target.value)}
            placeholder="Enter text"
            aria-label={s.label}
          />
        )}

        {s.type === TYPE_NUMBER && (
          <Input
            id={s.id}
            type="number"
            step="any"
            style={COMPACT_NUMERIC_INPUT_STYLE}
            value={(sectionValues?.[s.field] ?? "").toString()}
            onChange={(e) => onInputChanged?.(s.field, e.target.value)}
            placeholder="Enter Number"
            aria-label={s.label}
          />
        )}

        {s.type === TYPE_CURRENCY && (
          <Input
            id={s.id}
            type="number"
            step="0.01"
            style={COMPACT_NUMERIC_INPUT_STYLE}
            value={(sectionValues?.[s.field] ?? "").toString()}
            onChange={(e) => onInputChanged?.(s.field, e.target.value)}
            placeholder="Enter £"
            aria-label={s.label}
          />
        )}

        {s.type === TYPE_DATE && (
          <Input
            id={s.id}
            type="date"
            style={COMPACT_DATE_INPUT_STYLE}
            value={toDateInputValue(sectionValues?.[s.field])}
            onChange={(e) => onInputChanged?.(s.field, e.target.value)}
            aria-label={s.label}
            data-testid={`template-input-date-${s.field}`}
          />
        )}

        {s.type === "Lookup" &&
          (() => {
            const currentValue = (sectionValues?.[s.field] ?? "").toString();
            const normalizedOptions = normalizeOptionsToStrings(s.options);
            const hasCurrentValue = currentValue.trim().length > 0;
            const isInvalidCurrentValue = hasCurrentValue && !normalizedOptions.includes(currentValue);
            const lookupOptions = [
              ...(isInvalidCurrentValue
                ? [{ value: currentValue, label: `${currentValue} (Invalid)` }]
                : []),
              ...normalizedOptions.map((opt) => ({ value: opt, label: opt })),
            ];

            return (
              <CustomSelect
                inputId={s.id}
                value={currentValue}
                options={lookupOptions}
                onChange={(selected) => onInputChanged?.(s.field, selected?.value ?? "")}
                allowWhenLocked={allowWhenLocked}
                placeholder="Select Value"
                data-testid={`template-input-lookup-${s.field}`}
              />
            );
          })()}
      </div>
    </FormGroup>
  );

  // Visible Region: its own bordered box with a header row (label + switch) above its children.
  const renderRegionField = (s, depth) => {
    const isVisible = !!sectionValues?.[s.field];
    const hasChildren = Array.isArray(s.children) && s.children.length > 0;
    return (
      <div
        key={s.id}
        className={`template-region-group${isVisible && hasChildren ? " template-region-group--accent" : ""}${!isVisible ? " template-region--hidden" : ""}`}
        style={depth > 0 ? { marginLeft: `${depth * 1.5}rem` } : undefined}
      >
        <div
          className="template-region-legend"
          style={!isVisible || !hasChildren ? { border: "none", margin: 0, paddingBottom: 0 } : undefined}
        >
          <Label htmlFor={s.id} className="fw-semibold mb-0">
            {s.label}
          </Label>
          <FormGroup switch className="mb-0 d-flex align-items-center gap-2">
            <Input
              id={s.id}
              type="switch"
              checked={isVisible}
              onChange={(e) => onInputChanged?.(s.field, e.target.checked)}
            />
            {/* Fixed width so "Visible"/"Hidden" never changes the switch's rendered position. */}
            <span className="small text-muted" style={{ display: "inline-block", minWidth: "3.25rem" }}>
              {isVisible ? "Visible" : "Hidden"}
            </span>
          </FormGroup>
        </div>

        {/* Hidden regions show only the header, so the box appears collapsed; children are at
            depth 0 since they're already visually nested inside the box. */}
        {isVisible && Array.isArray(s.children) && renderFieldList(s.children, 0)}
      </div>
    );
  };

  // Region with no children: no box of its own — just a toggle row bundled with siblings.
  const renderRegionToggleRow = (s, isLast) => {
    const isVisible = !!sectionValues?.[s.field];
    return (
      <FormGroup key={s.id} className={`row align-items-center${!isVisible ? " template-region--hidden" : ""} ${isLast ? "mb-0" : "mb-3"}`}>
        <Label htmlFor={s.id} className="fw-semibold col-sm-4 col-md-4 mb-0 text-start">
          {s.label}
        </Label>
        <div className="col-sm-8 col-md-8 d-flex justify-content-end">
          <FormGroup switch className="mb-0 d-flex align-items-center gap-2">
            <Input
              id={s.id}
              type="switch"
              checked={isVisible}
              onChange={(e) => onInputChanged?.(s.field, e.target.checked)}
            />
            <span className="small text-muted" style={{ display: "inline-block", minWidth: "3.25rem" }}>
              {isVisible ? "Visible" : "Hidden"}
            </span>
          </FormGroup>
        </div>
      </FormGroup>
    );
  };

  // Subsection: its own bordered box with just a name header above its fields (no visibility toggle).
  const renderSubsectionField = (s) => (
    <div key={s.id} className="template-region-group template-region-group--accent">
      <div className="template-region-legend">
        <Label className="fw-semibold mb-0">{s.label}</Label>
      </div>
      {renderFieldList(s.children, 0)}
    </div>
  );

  // Groups consecutive siblings into a single bordered box; a Region breaks the run and gets its
  // own box whenever it has children (so it can show as a collapsed box), while a
  // childless Region is just a row bundled with the other fields.
  const renderFieldList = (items, depth = 0) => {
    const groups = [];
    let run = [];

    (items || []).forEach((item) => {
      if (item.type === "Subsection") {
        if (run.length) {
          groups.push({ type: "fields", items: run });
          run = [];
        }
        groups.push({ type: "subsection", item });
        return;
      }

      const hasChildren = item.type === "Region" && Array.isArray(item.children) && item.children.length > 0;
      if (hasChildren) {
        if (run.length) {
          groups.push({ type: "fields", items: run });
          run = [];
        }
        groups.push({ type: "region", item });
      } else {
        run.push(item);
      }
    });
    if (run.length) groups.push({ type: "fields", items: run });

    return groups.map((group) => {
      if (group.type === "subsection") {
        return renderSubsectionField(group.item);
      }

      if (group.type === "region") {
        return renderRegionField(group.item, depth);
      }

      return (
        <div
          key={`fields-${group.items[0].id}`}
          className="template-fields-group"
          style={depth > 0 ? { marginLeft: `${depth * 1.5}rem` } : undefined}
        >
          {group.items.map((s, idx) => {
            const isLast = idx === group.items.length - 1;
            return s.type === "Region"
              ? renderRegionToggleRow(s, isLast)
              : renderFieldRow(s, isLast);
          })}
        </div>
      );
    });
  };

  return (
    <Form className="d-flex flex-column h-100" style={{ minHeight: 0, paddingTop: "0.75rem" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          flex: "0 0 auto",
        }}
      >
        {!hideSectionTabs && (
          <div style={{ display: "inline-flex" }}>
            {sectionTabs.map((tab) => (
              <h6
                key={tab}
                className={activeSection === tab ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                style={{ cursor: "pointer" }}
                onClick={() => setActiveSection(tab)}
              >
                {tab}
              </h6>
            ))}
          </div>
        )}

        {saveStatus !== undefined && (
          <div className="d-flex align-items-center gap-2 mt-2 ms-auto">
            {saveStatus === null && (
              <span className="text-success small">
                Up to date <i className="bi bi-check-circle"></i>
              </span>
            )}
            {saveStatus === "saving" && (
              <span className="small">
                Currently Saving <Spinner size="sm" />
              </span>
            )}
            {saveStatus === "saved" && (
              <span className="text-success small">
                Saved <i className="bi bi-check-circle"></i>
              </span>
            )}
            {saveStatus === "error" && (
              <span className="text-danger small">
                Save failed <i className="bi bi-x-circle"></i>
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden", paddingRight: "0.25rem" }}>
        {renderFieldList(sectionsByTab?.get(activeSection) || [], 0)}
      </div>
    </Form>
  );
};

export default TemplateInputs;
